import React from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Tabs,
  Tab,
  Autocomplete,
  CircularProgress,
  Typography,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import { Search, PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const ControlPanel = ({
  batchMode,
  setBatchMode,
  symbol,
  setSymbol,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  lookbackDays,
  setLookbackDays,
  batchSymbols,
  setBatchSymbols,
  handleDetect,
  handleBatchDetect,
  detectMutation,
  batchDetectMutation,
  quickDatePresets,
  applyDatePreset,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  return (
    <Fade in timeout={800}>
      <Stack spacing={3}>
          {/* Mode Toggle - Tabs style */}
          <Box
            sx={{
              borderBottom: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
              mb: 3,
            }}
          >
            <Tabs
              value={batchMode ? 1 : 0}
              onChange={(e, newValue) => setBatchMode(newValue === 1)}
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  minHeight: 48,
                  px: 4,
                  py: 1.5,
                  borderRadius: 0,
                  transition: 'all 0.2s ease',
                  color: 'text.secondary',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    bgcolor: 'transparent',
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              <Tab label={t('downtrend.controlPanel.singleAnalysis')} />
              <Tab label={t('downtrend.controlPanel.batchAnalysis')} />
            </Tabs>
          </Box>

          {!batchMode ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'flex-end' },
                }}
              >
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                      <TextField
                        fullWidth
                        label={t('downtrend.controlPanel.symbol')}
                        value={symbol}
                        onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.background.paper, 1),
                            transition: 'all 0.2s ease',
                            '& fieldset': {
                              borderColor: alpha(theme.palette.divider, 0.3),
                              borderWidth: 1.5,
                            },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                            '&.Mui-focused': {
                              bgcolor: 'white',
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <Search
                              sx={{ 
                                mr: 1.5, 
                                color: 'text.secondary', 
                                fontSize: 20,
                                transition: 'color 0.2s ease',
                              }}
                            />
                          ),
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              fontWeight: 600,
                              fontSize: '0.95rem',
                            },
                          },
                        }}
                      />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' }, minWidth: 0 }}>
                      <TextField
                        fullWidth
                        label={t('downtrend.controlPanel.startDate')}
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.background.paper, 1),
                            transition: 'all 0.2s ease',
                            '& fieldset': {
                              borderColor: alpha(theme.palette.divider, 0.3),
                              borderWidth: 1.5,
                            },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                            '&.Mui-focused': {
                              bgcolor: 'white',
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            },
                          },
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              fontWeight: 500,
                              fontSize: '0.9rem',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' }, minWidth: 0 }}>
                      <TextField
                        fullWidth
                        label={t('downtrend.controlPanel.endDate')}
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.background.paper, 1),
                            transition: 'all 0.2s ease',
                            '& fieldset': {
                              borderColor: alpha(theme.palette.divider, 0.3),
                              borderWidth: 1.5,
                            },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                            '&.Mui-focused': {
                              bgcolor: 'white',
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            },
                          },
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              fontWeight: 500,
                              fontSize: '0.9rem',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 140px' }, minWidth: 0 }}>
                      <TextField
                        fullWidth
                        label={t('downtrend.controlPanel.lookbackDays')}
                        type="number"
                        value={lookbackDays}
                        onChange={(e) =>
                          setLookbackDays(parseInt(e.target.value) || 120)
                        }
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.background.paper, 1),
                            transition: 'all 0.2s ease',
                            '& fieldset': {
                              borderColor: alpha(theme.palette.divider, 0.3),
                              borderWidth: 1.5,
                            },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                            '&.Mui-focused': {
                              bgcolor: 'white',
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            },
                          },
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              fontWeight: 600,
                              fontSize: '0.95rem',
                            },
                          },
                        }}
                      />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { xs: '100%', sm: '160px' } }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleDetect}
                    disabled={detectMutation.isLoading || !symbol}
                    startIcon={
                      detectMutation.isLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <PlayArrow />
                      )
                    }
                    sx={{
                      height: 40,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      borderRadius: 1,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                        transform: 'translateY(-2px)',
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      },
                      '&:disabled': {
                        background: alpha(theme.palette.action.disabled, 0.12),
                        color: alpha(theme.palette.action.disabled, 0.5),
                      },
                    }}
                      >
                        {detectMutation.isLoading ? t('downtrend.controlPanel.analyzing') : t('downtrend.controlPanel.analyze')}
                      </Button>
                </Box>
              </Box>

              {/* Quick Date Presets - Compact inline */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  pt: 1,
                }}
              >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.8rem', 
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                      }}
                    >
                      {t('downtrend.controlPanel.quickPresets')}
                    </Typography>
                    {quickDatePresets.map((preset) => (
                      <Button
                        key={preset.days}
                        size="small"
                        variant="outlined"
                        onClick={() => applyDatePreset(preset.days)}
                        sx={{
                          textTransform: 'none',
                          minWidth: 'auto',
                          px: 2,
                          py: 0.75,
                          fontSize: '0.8rem',
                          height: 32,
                          fontWeight: 600,
                          borderRadius: 1,
                          borderWidth: 1.5,
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderWidth: 1.5,
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                          },
                        }}
                      >
                        {t(`downtrend.controlPanel.days${preset.days}`)}
                      </Button>
                    ))}
              </Box>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'flex-end' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={batchSymbols}
                    onChange={(event, newValue) => {
                      setBatchSymbols(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('downtrend.controlPanel.symbol')}
                        size="small"
                        placeholder={t('downtrend.controlPanel.symbolsPlaceholder')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.background.paper, 1),
                            transition: 'all 0.2s ease',
                            '& fieldset': {
                              borderColor: alpha(theme.palette.divider, 0.3),
                              borderWidth: 1.5,
                            },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                            '&.Mui-focused': {
                              bgcolor: 'white',
                              '& fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: 2,
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            },
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {value.map((option, index) => (
                          <Box
                            key={index}
                            {...getTagProps({ index })}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                borderColor: theme.palette.primary.main,
                              },
                            }}
                          >
                            {option}
                          </Box>
                        ))}
                      </Box>
                    )}
                  />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { xs: '100%', sm: '180px' } }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleBatchDetect}
                    disabled={
                      batchDetectMutation.isLoading || batchSymbols.length === 0
                    }
                    startIcon={
                      batchDetectMutation.isLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <PlayArrow />
                      )
                    }
                    sx={{
                      height: 40,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      borderRadius: 1,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                        transform: 'translateY(-2px)',
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      },
                      '&:disabled': {
                        background: alpha(theme.palette.action.disabled, 0.12),
                        color: alpha(theme.palette.action.disabled, 0.5),
                      },
                    }}
                      >
                        {batchDetectMutation.isLoading
                          ? t('downtrend.controlPanel.analyzing')
                          : t('downtrend.controlPanel.analyzeSymbols', { count: batchSymbols.length })}
                      </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Stack>
    </Fade>
  );
};

export default ControlPanel;

